import { useCallback, useMemo } from 'react';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { CriteoClient, CriteoRecommendationsResponse, type CriteoRequestContext } from '@jbhi-fi/apis/src/criteo';
import { CriteoPlacement, CriteoPlacementGroup, CriteoSponsoredProduct } from '@jbhi-fi/apis/src/criteo/types';
import { useFeatureFlag } from '../useFeatureFlag/useFeatureFlag';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { sha256 } from '@jbhi-fi/utils/src/sha256/sha256';
import { DEFAULT_SPONSORED_VARIATION_TYPE } from '../../utils/algolia/mergeCriteoWithAlgoliaHits';
import { useSponsoredVariationType } from '../useSponsoredResults/useSponsoredResults';

type GetSearchSponsoredProductsParams = {
  keywords: string;
  pageId?: string;
  filters?: string;
};

type GetCollectionSponsoredProductsParams = {
  collectionKey: string;
  pageId?: string;
  filters?: string;
};

type SponsoredProducts = Array<CriteoSponsoredProduct>;

type CriteoSponsoredProductsRequest = (
  criteoClient: CriteoClient,
  requestContext: CriteoRequestContext
) => ReturnType<CriteoClient['getSearchSponsoredProducts']>;

type UseCriteoSponsoredProductsReturn = {
  getSearchSponsoredProducts: (params: GetSearchSponsoredProductsParams) => Promise<SponsoredProducts>;
  getCollectionSponsoredProducts: (params: GetCollectionSponsoredProductsParams) => Promise<SponsoredProducts>;
  searchPageId: string;
  collectionPageId: string;
  isSponsoredContentEnabled: boolean;
  isSearchSponsoredProductsEnabled: boolean;
  isCollectionSponsoredProductsEnabled: boolean;
  isSponsoredContentResolved: boolean;
};

const SEARCH_PAGE_IDS = {
  desktop: 'viewSearchResult_API_desktop',
  mobile: 'viewSearchResult_API_mobile',
} as const;

const COLLECTION_PAGE_IDS = {
  desktop: 'viewCategory_API_desktop',
  mobile: 'viewCategory_API_mobile',
} as const;

const getPageIdForVariation = (basePageId: string, variationType: string): string =>
  variationType === DEFAULT_SPONSORED_VARIATION_TYPE ? basePageId : `${basePageId}_${variationType}`;

const getPlacementId = ({
  placementKey,
  groupIndex,
  placementIndex,
}: {
  placementKey: string;
  groupIndex: number;
  placementIndex: number;
}): string => [placementKey, groupIndex, placementIndex].join(':');

const GA_CLIENT_ID_MAX_ATTEMPTS = 4;

const getRetailerVisitorId = async (): Promise<string | undefined> => {
  for (let attempt = 0; attempt < GA_CLIENT_ID_MAX_ATTEMPTS; attempt += 1) {
    const retailerVisitorId = window?.ga?.getAll?.()?.[0]?.get?.('clientId') ?? undefined;

    if (retailerVisitorId || attempt === GA_CLIENT_ID_MAX_ATTEMPTS - 1) {
      return retailerVisitorId;
    }

    await new Promise(resolve => {
      window.setTimeout(resolve, 100);
    });
  }

  return undefined;
};

const getEmail = async (): Promise<string | undefined> => {
  const email = window?.Jbhifi?.customerEmail
    ?.trim()
    .replace(/[,'";]/g, '')
    .toLowerCase();

  if (!email) {
    return undefined;
  }

  try {
    return await sha256(email);
  } catch {
    return undefined;
  }
};

const getCriteoRequestContext = async (): Promise<CriteoRequestContext> => ({
  retailerVisitorId: await getRetailerVisitorId(),
  customerId: window?.Jbhifi?.customerId ? `${window.Jbhifi.customerId}` : undefined,
  email: await getEmail(),
});

const toSponsoredProductWithPlacement = ({
  placement,
  placementKey,
  groupIndex,
  placementIndex,
  product,
}: {
  placement: CriteoPlacement;
  placementKey: string;
  groupIndex: number;
  placementIndex: number;
  product: CriteoSponsoredProduct;
}): CriteoSponsoredProduct => ({
  ...product,
  placement: {
    id: getPlacementId({
      placementKey,
      groupIndex,
      placementIndex,
    }),
    OnLoadBeacon: placement.OnLoadBeacon,
    OnViewBeacon: placement.OnViewBeacon,
  },
});

const getSponsoredProducts = ({
  response,
}: {
  response?: CriteoRecommendationsResponse | unknown;
}): SponsoredProducts => {
  if (!response) {
    return [];
  }

  const placements = (response as CriteoRecommendationsResponse)?.placements ?? [];

  return placements.flatMap((placementGroup: CriteoPlacementGroup, groupIndex) =>
    Object.entries(placementGroup).flatMap(([placementKey, placementEntries]) =>
      placementEntries.flatMap((placement, placementIndex) =>
        placement.products.map(product =>
          toSponsoredProductWithPlacement({
            placement,
            placementKey,
            groupIndex,
            placementIndex,
            product,
          })
        )
      )
    )
  );
};

const fetchSponsoredProducts = async ({
  criteoClient,
  request,
}: {
  criteoClient: CriteoClient | null;
  request: CriteoSponsoredProductsRequest;
}): Promise<SponsoredProducts> => {
  if (!criteoClient) {
    return [];
  }

  const requestContext = await getCriteoRequestContext();
  const { data, error } = await request(criteoClient, requestContext);

  if (error || !data) {
    return [];
  }

  const sponsoredProducts = getSponsoredProducts({
    response: data,
  });
  return sponsoredProducts;
};

export const useCriteoSponsoredProducts = (): UseCriteoSponsoredProductsReturn => {
  const { criteoClient } = usePublicApiClient();
  const isSponsoredContentFlag = useFeatureFlag('isSponsoredContentEnabled');
  const isSponsoredContentEnabled = isSponsoredContentFlag ?? false;
  const isSponsoredContentResolved = isSponsoredContentFlag !== undefined;
  const isSearchSponsoredProductsFlag = useFeatureFlag('isSearchSponsoredProductsEnabled');
  const isCollectionSponsoredProductsFlag = useFeatureFlag('isCollectionSponsoredProductsEnabled');
  const isSearchSponsoredProductsEnabled = isSponsoredContentEnabled && (isSearchSponsoredProductsFlag ?? false);
  const isCollectionSponsoredProductsEnabled =
    isSponsoredContentEnabled && (isCollectionSponsoredProductsFlag ?? false);
  const sponsoredVariationType = useSponsoredVariationType();
  const isDesktop = useBreakpoint('lg', 'up');
  const pageIdVariant = isDesktop ? 'desktop' : 'mobile';
  const searchPageId = getPageIdForVariation(SEARCH_PAGE_IDS[pageIdVariant], sponsoredVariationType);
  const collectionPageId = getPageIdForVariation(COLLECTION_PAGE_IDS[pageIdVariant], sponsoredVariationType);
  const publicCriteoClient = useMemo(
    () => (criteoClient instanceof CriteoClient ? criteoClient : null),
    [criteoClient]
  );

  const getSearchSponsoredProducts = useCallback(
    async ({
      keywords,
      pageId = searchPageId,
      filters,
    }: GetSearchSponsoredProductsParams): Promise<SponsoredProducts> => {
      const trimmedKeywords = keywords.trim();
      if (!trimmedKeywords || !isSearchSponsoredProductsEnabled) {
        return [];
      }

      return await fetchSponsoredProducts({
        criteoClient: publicCriteoClient,
        request: (client, requestContext) =>
          client.getSearchSponsoredProducts({
            keywords: trimmedKeywords,
            pageId,
            filters,
            ...requestContext,
          }),
      });
    },
    [isSearchSponsoredProductsEnabled, publicCriteoClient, searchPageId]
  );

  const getCollectionSponsoredProducts = useCallback(
    async ({
      collectionKey,
      pageId = collectionPageId,
      filters,
    }: GetCollectionSponsoredProductsParams): Promise<SponsoredProducts> => {
      const trimmedCollectionKey = collectionKey.trim();

      if (!trimmedCollectionKey || !isCollectionSponsoredProductsEnabled) {
        return [];
      }

      return await fetchSponsoredProducts({
        criteoClient: publicCriteoClient,
        request: (client, requestContext) =>
          client.getCollectionSponsoredProducts({
            collectionKey: trimmedCollectionKey,
            pageId,
            filters,
            ...requestContext,
          }),
      });
    },
    [collectionPageId, publicCriteoClient, isCollectionSponsoredProductsEnabled]
  );

  return {
    getSearchSponsoredProducts,
    getCollectionSponsoredProducts,
    searchPageId,
    collectionPageId,
    isSponsoredContentEnabled,
    isSearchSponsoredProductsEnabled,
    isCollectionSponsoredProductsEnabled,
    isSponsoredContentResolved,
  };
};
